<template>
  <v-select
    v-model="selectedTags"
    class="mt-1 small-tag-text"
    :items="tagSelection"
    item-title="description"
    return-object
    multiple
    :density="density"
    :hide-details="hideDetails"
    variant="filled"
    :readonly="isReadOnly"
    :placeholder="placeholder"
    :label="label"
    :loading="tagsPending"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" :title="undefined">
        <template #prepend>
          <v-checkbox :model-value="selectedTags.some((tag) => tag._id === item._id)" hide-details density="compact" class="mr-1" />
        </template>
        <tag-badge :tag="item" />
      </v-list-item>
    </template>
    <template #subheader="{ props: headerProps }">
      <v-list-subheader v-bind="headerProps" />
    </template>

    <template #selection="{ item }">
      <tag-badge :tag="item" />
    </template>
  </v-select>
</template>

<script setup lang="ts" generic="TagSubsetType extends TagSubset">
const selectedTags = defineModel<Array<TagSubsetType>>({ default: () => [] })

const {
  documentId,
  getTagsFunction,
  isReadOnly,
  hiddenTagIds = [],
  organizationId = undefined,
  placeholder = undefined,
  label = undefined,
  hideDetails = false,
  density = "default",
} = defineProps<{
  getTagsFunction: GetTagsFunction<TagSubsetType>
  documentId: string
  organizationId?: string
  isReadOnly?: boolean
  hiddenTagIds?: Array<string>
  placeholder?: string
  label?: string
  hideDetails?: "auto" | boolean | undefined
  density?: "default" | "comfortable" | "compact"
}>()

const { t } = useI18n()

const authStore = useAuthStore()
const { getAllOrganizations } = useOrganizationStore()

const tagOptions = computed(() => allTags.value.filter((tag) => !hiddenTagIds?.includes(tag._id)))

const tagOrganizationIds = computed(() => [...new Set([authStore.sourceOrganization, organizationId].filter(Boolean))])
const tagOrganizations = ref<Array<Organization>>([])

const hasMultipleOrganizations = computed(() => tagOrganizationIds.value.length > 1)

const tagSelection = computed<Array<TagSubsetType>>(() => {
  // Only split up tags if there are multiple organizations, otherwise don't show headers
  if (!hasMultipleOrganizations.value) {
    return tagOptions.value
  }

  // Otherwise place all tags with the same source organisationId in
  const groupedTags = tagOptions.value.reduce<{
    internal: Array<TagSubsetType>
    external: Array<TagSubsetType>
  }>(
    ({ internal, external }, currentTag) => {
      const isInternalTag = currentTag.organization === authStore.sourceOrganization

      const currentTagGroups = {
        internal: [...internal],
        external: [...external],
      }

      currentTagGroups[isInternalTag ? "internal" : "external"].push(currentTag)

      return currentTagGroups
    },
    {
      internal: [],
      external: [],
    },
  )

  const internalOrganization = tagOrganizations.value.find((organization) => organization._id === authStore.sourceOrganization)
  const externalOrganization = tagOrganizations.value.find((organization) => organization._id === organizationId)

  const noTagsAvailable = {
    type: "subheader",
    class: ["font-italic", "opacity-80", "justify-center"],
    title: t("tags.noTagsAvailable"),
  }

  // Return tags split with the correct headers above them
  return [
    {
      type: "subheader",
      title: t("tags.organization", {
        organization: internalOrganization ? internalOrganization.description : t("tags.internal"),
      }),
    },
    ...(groupedTags.internal.length ? groupedTags.internal : [noTagsAvailable]),
    {
      type: "subheader",
      title: t("tags.organization", {
        organization: externalOrganization ? externalOrganization.description : t("tags.external"),
      }),
    },
    ...(groupedTags.external.length ? groupedTags.external : [noTagsAvailable]),
    // Force TypeScript to think this is a TagSubsetType array since that is the only thing that can be selected
  ] as unknown as Array<TagSubsetType>
})

watch(
  tagOrganizationIds,
  async () => {
    // We don't need organizations if we only have one
    if (!hasMultipleOrganizations.value) {
      return
    }

    tagOrganizations.value = await getAllOrganizations({
      _id: {
        $in: tagOrganizationIds.value,
      },
    })
  },
  { immediate: true },
)

const { data: allTags, pending: tagsPending } = useLazyAsyncData(
  `${documentId}-${tagOrganizationIds.value.join("-")}`,
  async () => getTagsFunction(false, { $in: tagOrganizationIds.value }),
  {
    default: () => [] as Array<TagSubsetType>,
  },
)
</script>
